import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { ref, createRef, type Ref } from 'lit/directives/ref.js';
import type * as Cesium from 'cesium';
import type { SatGlobe, Globe } from './globe/Globe';
import type { Clock } from './time/Clock';
import { hasWebGL } from './util/webgl';
import { getSharedObserver } from './observer/Observer';
import { readEmbeddedReplayContext, type ReplayContext } from './replay/replayContext';
import { parseShareParams } from './share/shareUrl';
import './ui/DetailPanel';
import './ui/Timeline';
import './ui/NoWebGL';
import './ui/ViewToggle';
import './ui/SkyOverlay';

@customElement('sat-app')
export class SatApp extends LitElement {
  @property({ type: String, attribute: 'cesium-ion-token' })
  cesiumIonToken = '';

  @property({ type: String, attribute: 'selected-norad' })
  selectedNorad: string | null = null;

  /** When set to "conjunction", the SPA enters replay mode using the
   *  JSON blob in #sat-replay-context. See docs/phase6.md. */
  @property({ type: String, attribute: 'replay-mode' })
  replayMode: string | null = null;

  /** NORAD of the currently-selected satellite, or null. */
  @state() private selected: number | null = null;

  /** Phase 7 chunk 1 — globe vs sky view. `sky` activates the stargazer
   *  camera pose at the observer's location (chunks 1B/1C wire the camera
   *  + topbar toggle; chunk 1A just stores the intent from the URL). */
  @state() private viewMode: 'globe' | 'sky' = 'globe';

  /** Phase 6 chunk 1 — populated when the route is /conjunction/p/s and the
   *  shell embedded a valid context blob. Drives ConjunctionScene activation. */
  @state() private replayContext: ReplayContext | null = null;

  /** Clock published by <sat-globe> once it's initialized. */
  @state() private clock: Clock | null = null;

  /** Set in connectedCallback. Drives the WebGL fallback branch in render(). */
  @state() private webglSupported = true;

  private globeRef: Ref<SatGlobe> = createRef();

  static styles = css`
    :host {
      display: block;
      position: absolute;
      inset: 0;
      overflow: hidden;
    }
    .layout {
      display: grid;
      grid-template-rows: auto 1fr auto;
      height: 100%;
      width: 100%;
    }
    .globe-area {
      position: relative;
      overflow: hidden;
      /* Trap child z-indexes (DetailPanel, ConjunctionHud, StationTooltip)
         inside this stacking context so they don't compete with the
         topbar's z-index in the parent grid. Without this, the panel's
         z-index: 20 leaks into the grid and overlays the topbar's
         dropdown menus. */
      isolation: isolate;
    }

    /* Phase 8 chunk 1 — reduced-motion preference. Per-component CSS
       elsewhere (DetailPanel, ShareButton) handles its own animations
       directly. This block exposes a custom property descendants can
       consume if they need a centrally-managed motion duration. */
    @media (prefers-reduced-motion: reduce) {
      :host {
        --motion-duration: 0ms;
      }
    }

    /* Phase 8 chunk 4 — backdrop dim painted behind the mobile
       bottom-sheet when it's at the "full" snap state, so the rest
       of the globe view recedes. Only applies at ≤ 700 px (where the
       sheet exists at all); other snap states leave the globe
       unobscured. z-index sits below the panel's z-index: 20. */
    @media (max-width: 700px) {
      :host([data-panel-snap='full']) .globe-area::before {
        content: '';
        position: absolute;
        inset: 0;
        background: rgba(0, 0, 0, 0.45);
        z-index: 19;
        pointer-events: none;
        transition: opacity 0.2s ease;
      }
    }
    @media (max-width: 700px) and (prefers-reduced-motion: reduce) {
      :host([data-panel-snap='full']) .globe-area::before {
        transition: none;
      }
    }
  `;

  connectedCallback(): void {
    super.connectedCallback();
    this.webglSupported = hasWebGL();
    if (this.selectedNorad !== null && /^\d+$/.test(this.selectedNorad)) {
      this.selected = parseInt(this.selectedNorad, 10);
    }
    this.addEventListener('select', this.handleSelect as EventListener);
    this.addEventListener('search-select', this.handleSearchSelect as EventListener);
    this.addEventListener('panel-close', this.handlePanelClose as EventListener);
    this.addEventListener('panel-snap-change', this.handlePanelSnapChange as EventListener);
    this.addEventListener('clock-ready', this.handleClockReady as EventListener);
    this.addEventListener('ribbon-orbits-change', this.handleRibbonOrbits as EventListener);
    // Phase 9 chunk 4 — globe-level decay-pick event (Globe.ts dispatches
    // it on the window; we listen window-wide).
    window.addEventListener('decay-pick', this.handleDecayPick as EventListener);

    // Phase 7 chunk 1C — auto-exit sky view if the user clears their
    // observer while in sky mode. Without an anchor the camera pose is
    // meaningless and the toggle becomes disabled, stranding them in
    // sky view with no exit affordance.
    this.unsubscribeObserver = getSharedObserver().subscribe((next) => {
      if (next === null && this.viewMode === 'sky') {
        this.requestViewMode('globe');
      }
    });

    // Phase 5 chunk 6 — deep-link restore from ?sat & lat & lon & alt & t.
    // Observer is set immediately; sat selection waits for the globe (which
    // mounts in updated()) and clock waits for the `clock-ready` event.
    this.applyShareParamsFromUrl();

    // Phase 6 chunk 1 — conjunction replay mode. Reads the JSON blob the
    // SpaShellController embedded; if absent or unparseable, the SPA boots
    // in normal mode (the `<sat-app>` element keeps `replay-mode` set
    // but `replayContext` stays null, so ConjunctionScene won't activate).
    if (this.replayMode === 'conjunction') {
      this.replayContext = readEmbeddedReplayContext();
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.removeEventListener('select', this.handleSelect as EventListener);
    this.removeEventListener('search-select', this.handleSearchSelect as EventListener);
    this.removeEventListener('panel-close', this.handlePanelClose as EventListener);
    this.removeEventListener('panel-snap-change', this.handlePanelSnapChange as EventListener);
    this.removeEventListener('clock-ready', this.handleClockReady as EventListener);
    this.removeEventListener('ribbon-orbits-change', this.handleRibbonOrbits as EventListener);
    window.removeEventListener('decay-pick', this.handleDecayPick as EventListener);
    this.unsubscribeObserver?.();
    this.unsubscribeObserver = null;
  }

  /** Phase 7 chunk 1C — observer-subscription cleanup handle. */
  private unsubscribeObserver: (() => void) | null = null;

  private handleClockReady = (e: CustomEvent<{ clock: Clock }>): void => {
    this.clock = e.detail.clock;
    // Globe is ready now — drain any deferred share-URL actions.
    if (this.pendingClockMs !== null) {
      e.detail.clock.setTimeMs(this.pendingClockMs);
      this.pendingClockMs = null;
    }
    if (this.pendingFlyToNorad !== null) {
      this.applySelection(this.pendingFlyToNorad, /* fly */ true);
      this.pendingFlyToNorad = null;
    }
    // Phase 6 chunk 1 — if the route was /conjunction/p/s and the shell
    // embedded a valid context blob, enter replay mode now that the
    // globe is initialized. Errors during scene activation are logged
    // and the SPA stays in normal mode — better than a blank page.
    if (this.replayContext !== null) {
      const globe = this.globeRef.value?.globe;
      void globe?.enterConjunctionReplay(this.replayContext).catch((err: unknown) => {
        console.warn('[sat-app] conjunction replay activation failed', err);
        this.replayContext = null;
      });
    }

    // Phase 7 chunk 1C — if applyShareParamsFromUrl restored viewMode='sky'
    // from a `?view=sky` deep-link, activate sky view now that the camera
    // is initialized. Skip when replay context is active (replay scene
    // owns the camera per docs/phase7.md § II decision 10).
    if (this.viewMode === 'sky' && this.replayContext === null) {
      this.activateSkyView();
    }
  };

  /** Pending share-URL state held until the globe's Clock dispatches `clock-ready`. */
  private pendingClockMs: number | null = null;
  private pendingFlyToNorad: number | null = null;

  private applyShareParamsFromUrl(): void {
    const params = parseShareParams(window.location.search);

    if (params.sat !== undefined && this.selected === null) {
      this.selected = params.sat;
      // Defer the camera fly + highlight until the globe is initialized.
      this.pendingFlyToNorad = params.sat;
    }

    if (params.lat !== undefined && params.lon !== undefined) {
      // Don't clobber an observer the user has already chosen (and which
      // localStorage will have restored before we run). Share-URL state
      // only wins when no observer is set yet.
      const obs = getSharedObserver();
      if (obs.getCurrent() === null) {
        obs.setManual(params.lat, params.lon, params.altMeters ?? 0, 'Shared location');
      }
    }

    if (params.t !== undefined) {
      const ms = Date.parse(params.t);
      if (!Number.isNaN(ms)) this.pendingClockMs = ms;
    }

    // Phase 7 chunk 1A — restore sky-view intent from URL. Only honored
    // when an observer is also available (URL or localStorage); otherwise
    // sky view has no anchor and stays at the globe default per
    // docs/phase7.md § II decision 9 "gracefully ignored if no observer
    // is available." Camera activation is chunks 1B/1C; 1A only sets
    // the state field.
    if (params.view === 'sky') {
      const hasUrlObserver = params.lat !== undefined && params.lon !== undefined;
      const hasStoredObserver = getSharedObserver().getCurrent() !== null;
      if (hasUrlObserver || hasStoredObserver) {
        this.viewMode = 'sky';
      }
    }
  }

  /** Click-on-globe → just set selection (no camera fly). */
  private handleSelect = (e: CustomEvent<{ norad: number | null }>): void => {
    this.applySelection(e.detail.norad, /* fly */ false);
  };

  /** Search picker → set selection AND fly the camera to it. */
  private handleSearchSelect = (e: CustomEvent<{ norad: number; name: string }>): void => {
    this.applySelection(e.detail.norad, /* fly */ true);
  };

  private handlePanelClose = (): void => {
    this.applySelection(null, false);
  };

  /**
   * Phase 9 chunk 4 — handle a click on a decay-trace polyline.
   * Routes to the satellite's text detail page (the closest thing
   * to "that reentry's detail page" — the per-norad text view shows
   * the decayed-sat metadata).
   */
  private handleDecayPick = (e: Event): void => {
    const detail = (e as CustomEvent<{ norad: number }>).detail;
    if (detail === undefined || detail === null || typeof detail.norad !== 'number') return;
    window.location.href = `/text/satellite/${detail.norad}`;
  };

  /**
   * Phase 8 chunk 4 — mirror the mobile bottom-sheet's snap state as a
   * `data-panel-snap` attribute on `<sat-app>` so the .globe-area
   * backdrop dim can be driven by CSS. Only the "full" state paints
   * the backdrop (mobile breakpoint gates it).
   */
  private handlePanelSnapChange = (e: CustomEvent<{ state: 'full' | 'mid' | 'peek' }>): void => {
    this.setAttribute('data-panel-snap', e.detail.state);
  };

  private handleRibbonOrbits = (e: CustomEvent<{ orbits: number }>): void => {
    this.globeRef.value?.globe.ribbons?.setOrbits(e.detail.orbits);
  };

  /** Phase 7 chunk 1A — read-only view-mode accessor. */
  public getViewMode(): 'globe' | 'sky' {
    return this.viewMode;
  }

  /** Phase 9 chunk 1 — read-only selection accessor (zoom button). */
  public getSelectedNorad(): number | null {
    return this.selected;
  }

  /** Phase 7 chunk 3D — accessor for child elements (like
   *  `<sat-search>`) that need to query the globe in sky view, e.g.
   *  for the above-horizon NORAD set to filter autocomplete results. */
  public getGlobe(): Globe | undefined {
    return this.globeRef.value?.globe;
  }

  /**
   * Phase 7 chunk 1C — request a view-mode flip. Idempotent for
   * no-op transitions. Drives the Globe's camera entry/exit and emits
   * a window-level `view-mode-changed` event so the topbar toggle
   * (and any future view-aware UI) can re-render.
   *
   * The actual sky-view entry requires both a ready globe + a non-null
   * observer. If either is missing, the state still flips so the user's
   * intent isn't lost — but no camera change happens until both
   * conditions are met.
   */
  public requestViewMode(target: 'globe' | 'sky'): void {
    if (target === this.viewMode) return;
    if (target === 'sky') {
      this.viewMode = 'sky';
      this.activateSkyView();
    } else {
      this.viewMode = 'globe';
      this.globeRef.value?.globe.exitSkyView();
    }
    window.dispatchEvent(
      new CustomEvent('view-mode-changed', { detail: { viewMode: this.viewMode } }),
    );
  }

  /**
   * Phase 11 follow-up — public API the chunk-2C sky-view compass HUD
   * uses to rotate the camera when the user clicks a direction cell.
   * Silent no-op outside sky view; pitch + FOV are preserved.
   */
  public requestSkyHeadingDeg(headingDeg: number): void {
    if (this.viewMode !== 'sky') return;
    this.globeRef.value?.globe.setSkyHeadingDeg(headingDeg);
  }

  /** Phase 7 chunk 1C — actually enter sky view when both globe + observer are ready.
   *  Called from `requestViewMode('sky')` and from `handleClockReady` for the
   *  URL-restore path. Silent no-op if either prerequisite is missing.
   *  Chunk 3C also seeds the selected-sat pass arc when a sat is already
   *  selected at entry time. */
  private activateSkyView(): void {
    const globe = this.globeRef.value?.globe;
    const obs = getSharedObserver().getCurrent();
    if (globe === undefined || obs === null) return;
    globe.enterSkyView(obs);
    if (this.selected !== null) {
      void globe.refreshSelectedPassArc(this.selected, obs);
    }
  }

  private applySelection(norad: number | null, fly: boolean): void {
    this.selected = norad;
    // Phase 9 chunk 1 — fan out a window-level event so the zoom
    // button (and any other selection-aware UI) can re-render
    // without polling.
    window.dispatchEvent(
      new CustomEvent('selection-changed', { detail: { norad } }),
    );
    const globe = this.globeRef.value?.globe;
    globe?.layer?.setHighlight(norad);
    globe?.setRibbonTarget(norad);
    globe?.setMarqueeTarget(norad);
    // Phase 7 chunk 3D — suppress the camera fly-to in sky view. The
    // sky-view camera is anchored at the observer's location looking
    // up; flying it to the satellite would silently teleport the user
    // out of sky-view framing while the UI still claimed "sky" mode
    // (the topbar toggle + floating overlay both stayed visible).
    // Selection in sky view still highlights, sets the ribbon/marquee,
    // and refreshes the pass arc — but the camera stays put.
    if (fly && norad !== null && this.viewMode === 'globe') {
      globe?.flyToSatellite(norad);
    }
    // Phase 7 chunk 3E — in sky view, rotate the camera so the
    // selected satellite is in frame. Only orientation changes;
    // the camera stays at the observer + eye-height. The selected
    // dot's chunk-3A pulse animation then makes the target legible.
    if (norad !== null && this.viewMode === 'sky') {
      globe?.aimSkyViewAt(norad);
    }
    // Phase 7 chunk 3C — refresh the selected-sat pass arc in sky view.
    // The Globe method is silent-no-op outside sky view, so we can
    // call it unconditionally.
    const obs = getSharedObserver().getCurrent();
    void globe?.refreshSelectedPassArc(norad, obs);
  }

  /** For <sat-detail-panel> — looks up live ECEF from the layer. */
  private getCurrentPosition = (norad: number): Cesium.Cartesian3 | null => {
    return this.globeRef.value?.globe.layer?.getPosition(norad) ?? null;
  };

  render() {
    if (!this.webglSupported) {
      // Per req_spec §24: WebGL unavailable → graceful fallback notice
      // pointing at the server-rendered text-only catalog at /text.
      return html`
        <div class="layout">
          <sat-top-bar></sat-top-bar>
          <sat-no-webgl></sat-no-webgl>
        </div>
      `;
    }
    return html`
      <div class="layout">
        <sat-top-bar></sat-top-bar>
        <div class="globe-area">
          <sat-globe
            cesium-ion-token=${this.cesiumIonToken}
            ${ref(this.globeRef)}
          ></sat-globe>
          <sat-detail-panel
            .norad=${this.selected}
            .getCurrentPosition=${this.getCurrentPosition}
          ></sat-detail-panel>
          ${this.replayContext !== null
            ? html`<sat-conjunction-hud
                .ctx=${this.replayContext}
                .globe=${this.globeRef.value?.globe ?? null}
              ></sat-conjunction-hud>`
            : nothing}
          <sat-sky-overlay></sat-sky-overlay>
        </div>
        <sat-timeline .clock=${this.clock}></sat-timeline>
        <sat-station-tooltip></sat-station-tooltip>
        <sat-amateur-station-tooltip></sat-amateur-station-tooltip>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sat-app': SatApp;
  }
}
